import { useEffect, useRef } from 'react';

import InfoIcon from '@/assets/info-shape.svg?react';
import PencilIcon from '@/assets/pencil.svg?react';
import Button from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import Modal, { ModalContent, ModalTitle } from '@/components/ui/Modal';

import {
  type CompanyInvoiceValues,
  isCompanyInvoiceComplete,
  missingCompanyDataFields,
  pickCompanyInvoice,
} from '../helpers/companyInvoice';
import type { ManualEntryReason } from './CompanyLookupField';
import InputApartmentNumber from './InputApartmentNumber';
import InputCompanyName from './InputCompanyName';
import InputInvoiceBuildingNumber from './InputInvoiceBuildingNumber';
import InputInvoiceCity from './InputInvoiceCity';
import InputInvoicePostalCode from './InputInvoicePostalCode';
import InputInvoiceStreet from './InputInvoiceStreet';
import { useLingui } from '@lingui/react';
import { FormProvider, useForm } from 'react-hook-form';

export type CompanyDataModalVariant = 'edit' | 'incomplete' | ManualEntryReason;

type CompanyDataFormValues = CompanyInvoiceValues & { is_company: boolean };

interface Props {
  open: boolean;
  variant: CompanyDataModalVariant;
  initialValues: CompanyInvoiceValues;
  onClose: () => void;
  onSave: (values: CompanyInvoiceValues) => void;
}

const Notice = ({
  tone,
  children,
}: {
  tone: 'warning' | 'info';
  children: string;
}) => (
  <div
    className={
      tone === 'warning'
        ? 'caption flex items-start gap-2 rounded-md border border-yellow-500 bg-yellow-50 px-3 py-[9px] text-dark-gray-500'
        : 'caption flex items-start gap-2 rounded-md bg-gray-200 px-3 py-2.5 text-dark-gray-500'
    }
  >
    <InfoIcon
      width={16}
      className={
        tone === 'warning'
          ? 'mt-0.5 shrink-0 [&>path]:fill-yellow-500'
          : 'mt-0.5 shrink-0 [&>path]:fill-blue-600'
      }
    />
    <p>{children}</p>
  </div>
);

const CompanyDataModal = ({
  open,
  variant,
  initialValues,
  onClose,
  onSave,
}: Props) => {
  const { i18n } = useLingui();
  const contentRef = useRef<HTMLDivElement>(null);
  const methods = useForm<CompanyDataFormValues>({
    defaultValues: { ...initialValues, is_company: true },
  });
  const { handleSubmit, trigger, watch } = methods;

  const isEdit = variant === 'edit';
  const isComplete = isCompanyInvoiceComplete(watch());

  useEffect(() => {
    if (variant === 'incomplete') {
      trigger(missingCompanyDataFields(initialValues));
    }
  }, [variant, initialValues, trigger]);

  const warningByVariant: Record<
    Exclude<CompanyDataModalVariant, 'edit'>,
    string
  > = {
    incomplete: i18n.t(
      'Nie wszystkie dane firmy udało się pobrać z GUS. Uzupełnij brakujące pola — są potrzebne do wystawienia faktury.'
    ),
    not_found: i18n.t(
      'Nie znaleźliśmy firmy w GUS. Uzupełnij dane firmy ręcznie — są potrzebne do wystawienia faktury.'
    ),
    unavailable: i18n.t(
      'Nie udało się pobrać danych z GUS. Uzupełnij dane firmy ręcznie — są potrzebne do wystawienia faktury.'
    ),
  };
  const warning = variant === 'edit' ? null : warningByVariant[variant];

  const onSubmit = (values: CompanyDataFormValues) => {
    onSave(pickCompanyInvoice({ ...values, tax_id: initialValues.tax_id }));
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <ModalContent
        ref={contentRef}
        aria-describedby={undefined}
        className="max-w-[500px] gap-0 overflow-hidden rounded-[20px] p-0 lg:p-0"
        onOpenAutoFocus={(event) => {
          // Fokus na samym oknie, nie na polu: w polu z fokusem ramka robi się
          // niebieska i zasłania czerwoną ramkę brakujących danych z Figmy.
          event.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <div className="flex shrink-0 justify-center border-b border-dark-gray-100 px-6 pb-5 pt-6 lg:pb-6">
          <ModalTitle asChild>
            <h3 className="h3 flex items-center gap-2 text-blue-600">
              <PencilIcon width={24} className="shrink-0" />
              {isEdit
                ? i18n.t('Edytuj dane firmy')
                : i18n.t('Uzupełnij dane firmy')}
            </h3>
          </ModalTitle>
        </div>

        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
              {warning && (
                <Notice tone={isComplete ? 'info' : 'warning'}>
                  {isComplete
                    ? i18n.t(
                        'Dziękujemy za uzupełnienie brakujących danych. Sprawdź je i zapisz — pojawią się na fakturze.'
                      )
                    : warning}
                </Notice>
              )}

              <div className="flex flex-col gap-3">
                <InputCompanyName />

                <Input
                  label={i18n.t('NIP')}
                  value={initialValues.tax_id}
                  data-testid="company-data-modal-tax_id-input"
                  disabled
                  readOnly
                />

                <Notice tone="info">
                  {isEdit || variant === 'incomplete'
                    ? i18n.t(
                        'Aby podać dane innej firmy, zamknij okno i wybierz „Zmień NIP”.'
                      )
                    : i18n.t('Aby zmienić NIP, zamknij okno i popraw numer.')}
                </Notice>

                <InputInvoiceStreet />

                <div className="grid grid-cols-2 gap-3">
                  <InputInvoiceBuildingNumber />
                  <InputApartmentNumber name="invoice_apartment_number" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <InputInvoicePostalCode />
                  <InputInvoiceCity />
                </div>
              </div>
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-5 border-t border-dark-gray-100 bg-gray-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
              <Button
                size="small"
                type="button"
                intent="secondary"
                className="hover:bg-gray-400 hover:text-blue-600"
                onClick={onClose}
              >
                {i18n.t('Anuluj')}
              </Button>
              <Button
                size="small"
                type="submit"
                disabled={!isEdit && !isComplete}
              >
                {isEdit ? i18n.t('Zapisz zmiany') : i18n.t('Zapisz dane')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

export default CompanyDataModal;
